import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import MainAppRouter from "./pages/MainAppRouter";
// AdminPage is lazy-loaded — only fetched when user navigates to /admin (admins only, ~30-50 KB saved)
const AdminPage = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/AdminPage"));
// SecuritySettingsPage is lazy-loaded — only fetched when user opens /settings/security
const SecuritySettingsPage = lazy(() => import(/* webpackChunkName: "settings" */ "./pages/SecuritySettingsPage"));
// HolterPage is lazy-loaded — only fetched when user opens /holter (opt-in module)
const HolterPage = lazy(() => import(/* webpackChunkName: "holter" */ "./pages/HolterPage"));
import CapturePage from "./pages/CapturePage";
import SharedVideoPage from "./pages/SharedVideoPage";
import SharedAdvicePage from "./pages/SharedAdvicePage";
import PrivacyPage from "./pages/PrivacyPage";
import BilanPatientPage from "./pages/bilan/BilanPatientPage";
import BilanFichePatientView from "./pages/bilan/BilanFichePatientView";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SessionExpiredBanner from "./components/SessionExpiredBanner";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!user) {
    // Preserve sidepanel mode across login redirect
    const params = new URLSearchParams(window.location.search);
    const isSidepanel = params.get('mode') === 'sidepanel' || window.self !== window.top;
    const loginPath = isSidepanel ? '/login?mode=sidepanel' : '/login';
    return <Navigate to={loginPath} replace />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRouter() {
  return (
    <>
      <SessionExpiredBanner />
      <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainAppRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/holter"
        element={
          <ProtectedRoute>
            <Suspense fallback={
              <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            }>
              <HolterPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Suspense fallback={
              <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            }>
              <AdminPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/security"
        element={
          <ProtectedRoute>
            <Suspense fallback={
              <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            }>
              <SecuritySettingsPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capture/:sessionId"
        element={<CapturePage />}
      />
      <Route
        path="/v/:token"
        element={<SharedVideoPage />}
      />
      <Route
        path="/c/:token"
        element={<SharedAdvicePage />}
      />
      <Route
        path="/privacy"
        element={<PrivacyPage />}
      />
      <Route
        path="/reset-password/:token"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      <Route
        path="/bilan/fiche/:token"
        element={<BilanFichePatientView />}
      />
      <Route
        path="/bilan/:slug"
        element={<BilanPatientPage />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

import { ErrorBoundary } from "./components/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./utils/axiosSetup"; // registers global axios-retry (idempotent)

// Shared QueryClient — 5min stale, 30min GC, no refetch on window focus by default.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <div className="App">
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
            </AuthProvider>
          </BrowserRouter>
        </div>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
